import { ProviderRole } from '@enums/ProviderRole';
import formatProviderName from '@utils/formatProviderName';
import { titleCase } from 'title-case';
import { getDrxDeepLink } from '../../../../utils/config';

type InNetworkCoverage = {
  providers: [any];
  prescriptions: [any];
};

export enum FilterType {
  prescriptions = 'prescriptions',
  specialistProviders = 'specialistProviders',
  primaryProviders = 'primaryProviders',
}

export type Filter = {
  name?: string;
  value: string;
  type: FilterType;
  checked: boolean;
  inNetworkPercentage?: number;
};

/*
 * Get inNetworkPercentage for a provider.
 */
const getInNetworkPercentageForProvider = (provider: any, inNetworkCoverage: InNetworkCoverage) => {
  return inNetworkCoverage?.providers.find((iterProvider: any) => iterProvider.id === provider.id)?.inNetworkPercentage;
};

/*
 * Get inNetworkPercentage for a prescription.
 */
export const getInNetworkPercentageForPrescription = (prescription: any, inNetworkCoverage: InNetworkCoverage) => {
  return inNetworkCoverage?.prescriptions.find((iterPrescription: any) => iterPrescription.id === prescription.id)
    ?.inNetworkPercentage;
};

/*
 * Create filters object from initial user data.
 */
export const createFilters = (prescriptions: [any], providers: [any], inNetworkCoverage?: InNetworkCoverage) => {
  const filters: {
    primaries: Filter[];
    specialists: Filter[];
    prescriptions: Filter[];
  } = {
    primaries: [],
    prescriptions: [],
    specialists: [],
  };

  filters.prescriptions = prescriptions.map((prescription: any) => ({
    type: FilterType.prescriptions,
    name: titleCase(prescription.label_name.toLowerCase()),
    value: prescription.id,
    checked: !!prescription.lapro_active,
    inNetworkPercentage: getInNetworkPercentageForPrescription(prescription, inNetworkCoverage),
  }));

  filters.primaries = providers
    .filter((provider: any) => provider.provider_role === ProviderRole.PRIMARY)
    .map((provider: any) => {
      return {
        type: FilterType.primaryProviders,
        name: formatProviderName(provider),
        value: provider.id,
        checked: !!provider.lapro_active,
        inNetworkPercentage: getInNetworkPercentageForProvider(provider, inNetworkCoverage),
      };
    });

  // Some providers have provider_role = NULL, let's add those
  // to the list of specialists.
  filters.specialists = providers
    .filter(
      (provider: any) =>
        provider.provider_role === ProviderRole.SPECIALIST ||
        provider.provider_role === ProviderRole.HOSPITAL ||
        !provider.provider_role
    )
    .map((provider: any) => ({
      type: FilterType.specialistProviders,
      name: formatProviderName(provider),
      value: provider.id,
      checked: !!provider.lapro_active,
      inNetworkPercentage: getInNetworkPercentageForProvider(provider, inNetworkCoverage),
    }));

  return filters;
};

export const getRelayState = (year: number, plans, zip: string, fips: string) => {
  const base = getDrxDeepLink(year);

  return plans.length === 1
    ? `${base}/Shopping/PlanDetails?ZipCode=${zip}&CountyFIPS=${fips}&PlanID=${plans}`
    : `${base}/Shopping/ComparePlans?ZipCode=${zip}&CountyFIPS=${fips}&PlanID=${plans}`;
};

export const shouldRenderBadge = (filter: Filter): boolean => {
  if (filter.type === FilterType.prescriptions) {
    if (filter.inNetworkPercentage === 0) {
      return true;
    }
    return false;
  }

  if (filter.inNetworkPercentage === undefined) {
    return false;
  }

  return true;
};

export const getCoverageBadgeColor = (filter: Filter) => {
  if (!filter.inNetworkPercentage) {
    return 'red';
  }

  if (filter.inNetworkPercentage >= 0.1) {
    return 'green';
  }

  return 'orange';
};

export const getCoverageBadgeText = (filter: Filter) => {
  if (!filter.inNetworkPercentage) {
    return filter.type === FilterType.prescriptions ? 'Not Covered by Medicare' : 'No Plans Cover';
  }

  if (filter.inNetworkPercentage >= 0.1) {
    return 'Many Plans Cover';
  }

  return 'Some Plans Cover';
};

/*
 * Create a comprehensive filter persistence object for localStorage.
 * This preserves badge data for all filter types when items become inactive.
 */
export const createFilterPersistenceData = (filters: {
  primaries: Filter[];
  specialists: Filter[];
  prescriptions: Filter[];
}) => {
  const persistenceData: {
    prescriptions: { [key: string]: number | undefined };
    providers: { [key: string]: number | undefined };
  } = {
    prescriptions: {},
    providers: {},
  };

  // Store prescription data
  filters.prescriptions.forEach((filter) => {
    persistenceData.prescriptions[filter.value] = filter.inNetworkPercentage;
  });

  // Store provider data (both primary and specialist)
  [...filters.primaries, ...filters.specialists].forEach((filter) => {
    persistenceData.providers[filter.value] = filter.inNetworkPercentage;
  });

  return persistenceData;
};

/*
 * Restore filter data from localStorage persistence.
 */
export const restoreFilterData = (
  newFilters: {
    primaries: Filter[];
    specialists: Filter[];
    prescriptions: Filter[];
  },
  persistenceData: any
) => {
  if (!persistenceData) return newFilters;

  // Restore prescription data
  if (persistenceData.prescriptions) {
    Object.keys(persistenceData.prescriptions).forEach((id) => {
      const matchingFilter = newFilters.prescriptions.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.prescriptions[id];
      }
    });
  }

  // Restore provider data
  if (persistenceData.providers) {
    Object.keys(persistenceData.providers).forEach((id) => {
      // Check primary providers
      let matchingFilter = newFilters.primaries.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.providers[id];
      }

      // Check specialist providers
      matchingFilter = newFilters.specialists.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.providers[id];
      }
    });
  }

  return newFilters;
};
